import React from 'react';
import { useCurrentEditor, useEditorState } from '@tiptap/react';
import { findParentNode } from '@tiptap/core';
import { FormLabel, IconButton, Stack, SvgIcon, Tooltip, Typography } from '@mui/material';
import BorderAllIcon from '@mui/icons-material/BorderAll';
import BorderBottomIcon from '@mui/icons-material/BorderBottom';
import BorderClearIcon from '@mui/icons-material/BorderClear';
import BorderLeftIcon from '@mui/icons-material/BorderLeft';
import BorderOuterIcon from '@mui/icons-material/BorderOuter';
import BorderRightIcon from '@mui/icons-material/BorderRight';
import BorderColorIcon from '@mui/icons-material/BorderColor';
import BorderTopIcon from '@mui/icons-material/BorderTop';
import BorderSelect from './BorderSelect';
import BorderWidthSelect from './BorderWidthSelect';
import { BORDER_COLOUR } from '../Extensions/data';
import TableColourPicker from './TableColourPicker';

const WrappedIcon = ({ Icon, ...props }) => {
	return (
		<SvgIcon {...props}>
			<Icon />
		</SvgIcon>
	);
};

const TooltipWrapper = ({ title, children }) => {
	return (
		<Tooltip title={title} disableInteractive>
			<span>{children}</span>
		</Tooltip>
	);
};

const TooltipIcon = ({ title, onClick, children, disabled, ...props }) => {
	return (
		<TooltipWrapper title={title}>
			<IconButton onClick={onClick} size='small' disabled={disabled} {...props}>
				{children}
			</IconButton>
		</TooltipWrapper>
	);
};

export const isMenuVisible = ({ editor, state, view }) => {
	if (!['div'].some((v) => editor?.isActive(v))) return false;
	const { from } = state.selection;

	const resolved = view.domAtPos(from);
	const domNode = resolved?.node;
	if (!domNode) return false;

	let el = domNode.nodeType === 1 ? domNode : domNode.parentElement;

	const editorConfig = editor?.storage?.config?.editor ?? {};

	while (el) {
		let editable = true;
		if (typeof editorConfig.editable === 'function') {
			editable = editorConfig.editable(el?.attributes);
		} else if (editorConfig.editable === false) {
			editable = false;
		}

		if (!editable) {
			return false;
		}

		if (el.getAttribute?.('data-readonly') === 'true') {
			return false;
		}

		el = el.parentElement;
	}

	return true;
};

const DivMenu = () => {
	const { editor } = useCurrentEditor();

	const { menuVisible, currentDiv } = useEditorState({
		editor,
		selector: (ctx) => {
			const selection = ctx.editor?.state?.selection;

			const menuVisible = isMenuVisible({
				editor: ctx.editor,
				state: ctx.editor?.state,
				view: ctx.editor?.view,
			});

			return {
				isActive: ['div'].some((v) => ctx.editor?.isActive(v)),
				currentSelection: selection,
				currentDiv: findParentNode((node) => ['div'].includes(node.type.name))(selection),
				menuVisible,
				mergeable: selection.ranges.length > 1,
			};
		},
	});

	if (!menuVisible) return <></>;

	return (
		<Stack spacing={1}>
			<Typography sx={{ fontSize: '10px', fontWeight: 'bold' }}>Container</Typography>
			<Stack
				direction='row'
				rowGap={1}
				columnGap={1}
				useFlexGap
				alignItems={'center'}
				sx={(theme) => ({
					flexWrap: 'wrap',
					width: '100%',

					'& .MuiIconButton-root': {
						padding: '0px',
						transition: 'color 0.15s ease',

						'&:hover': {
							backgroundColor: 'transparent !important',
							color: theme.palette.primary.main,
						},

						'&.active, &.is-active': {
							color: theme.palette.primary.main,
						},

						'& .MuiTouchRipple-root': {
							display: 'none',
						},
					},
					'& .MuiSvgIcon-root': {
						fontSize: '22px',
					},
				})}
			>
				<TooltipWrapper title='Set container background colour'>
					<TableColourPicker nodeType='div' />
				</TooltipWrapper>
				<TooltipIcon
					title='Container border outer'
					onClick={() =>
						editor
							.chain()
							.focus()
							.setDivBorders({ top: true, bottom: true, left: true, right: true })
							.run()
					}
				>
					<BorderOuterIcon />
				</TooltipIcon>
				<TooltipIcon
					title='Container border clear'
					onClick={() =>
						editor
							.chain()
							.focus()
							.setDivAttribute('borderColor', BORDER_COLOUR)
							.setDivAttribute('borderWidth', '1px')
							.setDivAttribute('style', 'solid')
							.setDivBorders({
								top: false,
								bottom: false,
								left: false,
								right: false,
							})
							.run()
					}
				>
					<BorderClearIcon />
				</TooltipIcon>

				<TooltipIcon
					title='Container border bottom'
					onClick={() => editor.chain().focus().setCellBorders({ bottom: true }).run()}
				>
					<BorderBottomIcon />
				</TooltipIcon>
				<TooltipIcon
					title='Container border top'
					onClick={() => editor.chain().focus().setCellBorders({ top: true }).run()}
				>
					<BorderTopIcon />
				</TooltipIcon>
				<TooltipIcon
					title='Container border left'
					onClick={() => editor.chain().focus().setCellBorders({ left: true }).run()}
				>
					<BorderLeftIcon />
				</TooltipIcon>
				<TooltipIcon
					title='Container border right'
					onClick={() => editor.chain().focus().setCellBorders({ right: true }).run()}
				>
					<BorderRightIcon />
				</TooltipIcon>
				<TooltipWrapper title='Container border colour'>
					<TableColourPicker
						nodeType='div'
						Icon={BorderColorIcon}
						attribute='borderColor'
					/>
				</TooltipWrapper>
				<TooltipWrapper title='Container border style'>
					<BorderSelect nodeType={'div'} />
				</TooltipWrapper>
				<TooltipWrapper title='Container border width'>
					<BorderWidthSelect nodeType={'div'} />
				</TooltipWrapper>
			</Stack>
		</Stack>
	);
};

export default DivMenu;
